/**
 * useCoachAccess — Route guard hook for coach_pro tier screens.
 *
 * Returns hasAccess: true for coach_pro AND org_pro (club) users.
 * org_pro has rank >= coach_pro in the tier hierarchy, so org members who
 * also coach are not blocked.
 *
 * Usage in each canonical coach screen:
 *   const { hasAccess, isLoading } = useCoachAccess();
 *   useEffect(() => {
 *     if (!isLoading && !hasAccess) router.replace('/(tabs)/home');
 *   }, [isLoading, hasAccess]);
 *   if (isLoading || !hasAccess) return null;
 */

import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';

export interface CoachAccessResult {
  /** True if the user has coach_pro or higher (org_pro) tier. */
  hasAccess: boolean;
  /** True while the auth profile is still loading. Guard should wait. */
  isLoading: boolean;
}

export function useCoachAccess(): CoachAccessResult {
  const { profileLoading } = useAuth();
  const { isCoachPlus } = useSubscription();
  return {
    hasAccess: isCoachPlus,
    isLoading: profileLoading,
  };
}
